const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

let authToken: string | null = (() => {
  try {
    const storedAuth = localStorage.getItem("auth-storage");
    if (storedAuth) {
      const parsedAuth = JSON.parse(storedAuth);
      return parsedAuth.state?.token || null;
    }
  } catch {
    // If parsing fails, return null
  }
  return null;
})();

export class ApiError extends Error {
  status: number;
  data?: any;
  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

function getHumanReadableError(status: number, responseText: string): string {
  const errorMessages: Record<number, string> = {
    400: "Please check your input",
    401: "Username or password is incorrect",
    403: "Access denied",
    404: "Information not found",
    408: "Request timed out",
    409: "Nickname already taken",
    422: "Please check your input",
    429: "Too many requests. Please wait",
    500: "Server error. Try again later",
    502: "Service unavailable",
    503: "Service unavailable",
    504: "Service unavailable",
  };

  try {
    const errorData = JSON.parse(responseText);
    if (typeof errorData?.message === "string") return errorData.message;
    if (errorData?.message?.toLowerCase?.().includes("invalid credentials")) {
      return "Username or password is incorrect";
    }
  } catch {
    // Continue with default handling
  }

  return errorMessages[status] || "Something went wrong";
}

let onTokenExpired: (() => void) | null = null;
type ApiResponseType = "json" | "blob";

async function apiRequest(
  endpoint: string,
  options: RequestInit = {},
  responseType: ApiResponseType = "json"
) {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (options.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  let response: Response;

  try {
    response = await fetch(url, {
      ...options,
      headers,
    });
  } catch {
    throw new ApiError(
      0,
      "Network error. Please check your connection and try again."
    );
  }

  if (!response.ok) {
    const text = await response.text();
    let data: any = undefined;
    try {
      data = JSON.parse(text);
    } catch {
      /* empty */
    }

    if (response.status === 401 && authToken) {
      authToken = null;
      
      if (onTokenExpired) {
        onTokenExpired();
      }
      
      throw new ApiError(
        401,
        "Your session has expired. Please login again.",
        data
      );
    }

    const humanReadableMessage = getHumanReadableError(response.status, text);
    throw new ApiError(response.status, humanReadableMessage, data);
  }

  // handle 204 No Content responses
  if (response.status === 204) {
    return undefined;
  }

  if (responseType === "blob") {
    return response.blob();
  }

  const text = await response.text();
  if (!text || !text.trim()) return null;
  return JSON.parse(text);
}

export const api = {
  setToken: (token: string | null) => {
    authToken = token;
  },

  setTokenExpiredCallback: (callback: (() => void) | null) => {
    onTokenExpired = callback;
  },

  post: (endpoint: string, data?: unknown) =>
    apiRequest(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    }),

  get: (endpoint: string) => apiRequest(endpoint, { method: "GET" }),

  getBlob: (endpoint: string) =>
    apiRequest(endpoint, { method: "GET" }, "blob") as Promise<Blob>,

  put: (endpoint: string, data?: unknown) =>
    apiRequest(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: (endpoint: string, data?: unknown) =>
    apiRequest(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: (endpoint: string) => apiRequest(endpoint, { method: "DELETE" }),
};
